import { Component, createRef } from 'preact';
import { sorceryImageUrl } from '../utils/cardImageUrl';

// Real-world alpha precon box proportions are roughly
// 64mm (w) x 89mm (h) x 22mm (d). Expressed in cqw so the
// box scales 1:1 with the canvas. Sized to fit 4-per-row in
// the store's max-w-[56cqw] grid.
const BOX_W = 9;      // cqw
const BOX_H = 12.5;   // cqw. 1 : 1.39 portrait ratio
const BOX_D = 3.1;    // cqw

const DEFAULT_TILT = { x: 8, y: -22 };   // degrees. Idle pose
const HOVER_TILT_MAX = { x: 14, y: 18 }; // extra travel under cursor

// Shared texture stack for the unprinted box stock. Warm aged book
// cloth with chiselled noise. Visible at all times, not crushed to
// black by ambient shading.
const STOCK_BASE = {
  background:
    `url("/tex-chisel.webp"), url("/tex-noise.webp"), url("/tex-stone.webp"), #4a3a2c`,
  backgroundBlendMode: 'overlay, soft-light, multiply, normal',
  backgroundSize: '180px 180px, 90px 90px, 320px 320px, auto',
};

// Per-face directional tint, applied on top of STOCK_BASE so the
// implied light source (front-left, slightly above) reads correctly
// at the default tilt without needing actual lighting.
const SIDE_SHADE = {
  // Right face faces away from the light → cooler & darker on the
  // far side, brighter near the front edge.
  background:
    'linear-gradient(90deg, rgba(255,220,170,0.18) 0%, rgba(0,0,0,0.55) 100%)',
  mixBlendMode: 'multiply',
};
const LEFT_SHADE = {
  background:
    'linear-gradient(90deg, rgba(0,0,0,0.55) 0%, rgba(255,220,170,0.18) 100%)',
  mixBlendMode: 'multiply',
};
const TOP_SHADE = {
  // Top catches the most light.
  background:
    'linear-gradient(180deg, rgba(255,230,180,0.22) 0%, rgba(0,0,0,0.35) 100%)',
  mixBlendMode: 'multiply',
};
const BOTTOM_SHADE = {
  background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.9) 100%)',
  mixBlendMode: 'multiply',
};

// Lid sleeve sits on top of the tray, ~30% of the box height. The
// sleeve overhangs by a hair. We fake the overhang with a thin
// inset highlight + groove shadow at the seam line.
const LID_SEAM_TOP = '32%';

export default class PreconDeckBox3D extends Component {
  state = { tiltX: DEFAULT_TILT.x, tiltY: DEFAULT_TILT.y, lifted: false, subtitleScale: 1 };
  subtitleRef = createRef();

  componentDidMount() {
    this.measureSubtitle();
    // FantaisieArtistique loads via @font-face. First paint may use
    // the fallback font, which gives a wrong natural width. Re-measure
    // once the real font is ready.
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(this.measureSubtitle);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.subtitle !== this.props.subtitle) this.measureSubtitle();
  }

  measureSubtitle = () => {
    const el = this.subtitleRef.current;
    if (!el?.parentElement) return;
    const parentWidth = el.parentElement.clientWidth;
    // Recover base-scale natural width from the currently-rendered
    // offsetWidth. We're scaling via font-size, so offsetWidth is
    // (baseNatural * currentScale). Dividing back out yields a
    // stable measurement that converges in one round-trip.
    const currentScale = this.state.subtitleScale || 1;
    const naturalWidth = el.offsetWidth / currentScale;
    if (parentWidth <= 0 || naturalWidth <= 0) return;
    const scale = Math.min(1, parentWidth / naturalWidth);
    if (Math.abs(scale - currentScale) > 0.001) {
      this.setState({ subtitleScale: scale });
    }
  };

  handleMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width - 0.5;
    const ny = (e.clientY - r.top)  / r.height - 0.5;
    this.setState({
      tiltX: DEFAULT_TILT.x - ny * HOVER_TILT_MAX.x,
      tiltY: DEFAULT_TILT.y + nx * HOVER_TILT_MAX.y,
      lifted: true,
    });
  };

  handleLeave = () => {
    this.setState({ tiltX: DEFAULT_TILT.x, tiltY: DEFAULT_TILT.y, lifted: false });
  };

  // Renders one of the four unprinted box faces (left/right/top/bottom).
  // `width`/`height` are in cqw. `edgeHighlight` is the side of the
  // face that touches the printed front cover, so a thin gold inlay
  // there sells the seam between cover and stock.
  renderSidePanel({ transform, width, height, shade, edgeHighlight, withSeam }) {
    const edgeInlay = {
      left:   'inset 1px 0 0 rgba(255,220,140,0.35)',
      right:  'inset -1px 0 0 rgba(255,220,140,0.35)',
      top:    'inset 0 1px 0 rgba(255,220,140,0.35)',
      bottom: 'inset 0 -1px 0 rgba(255,220,140,0.35)',
    }[edgeHighlight];

    return (
      <div
        style={{
          position: 'absolute',
          left: 0,
          top:  0,
          width:  `${width}cqw`,
          height: `${height}cqw`,
          marginLeft: `${-width / 2}cqw`,
          marginTop:  `${-height / 2}cqw`,
          transform,
          backfaceVisibility: 'hidden',
          overflow: 'hidden',
          ...STOCK_BASE,
          boxShadow:
            `${edgeInlay},` +
            'inset 0 0 0 1px rgba(0,0,0,0.55)',
        }}
      >
        {/* Directional shade overlay. Sells where the light is */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            ...shade,
            pointerEvents: 'none',
          }}
        />

        {withSeam ? (
          <>
            {/* Recessed groove where the lid sleeve meets the tray.
                A 1-px dark hairline with a brighter highlight just
                above it reads as a real overhanging lip. */}
            <div
              style={{
                position: 'absolute',
                left: 0, right: 0,
                top: `calc(${LID_SEAM_TOP} - 1px)`,
                height: '1px',
                background: 'rgba(255,225,180,0.32)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 0, right: 0,
                top: LID_SEAM_TOP,
                height: '2px',
                background: 'rgba(0,0,0,0.7)',
                boxShadow: '0 1px 0 rgba(255,225,180,0.12)',
                pointerEvents: 'none',
              }}
            />
          </>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      artSlug,
      wordmark = 'Sorcery',
      subtitle = 'Contested Realm',
      accent  = '#7fc3ff',          // element-tinted glow; Air → sky blue
      onClick,
    } = this.props;
    const { tiltX, tiltY, lifted } = this.state;

    const artUrl = artSlug ? sorceryImageUrl(artSlug) : null;

    // Each face is positioned at the box centre using
    // `left/top: 50%` plus negative margins equal to half its own
    // size. That pins its center at the origin so the post-rotation
    // translateZ() is the only thing that moves it. Avoids the
    // off-by-W/2 errors you get when mixing edge anchors with rotated
    // transforms.
    const halfW = BOX_W / 2;
    const halfH = BOX_H / 2;
    const halfD = BOX_D / 2;

    return (
      <button
        type="button"
        onClick={onClick}
        onMouseMove={this.handleMove}
        onMouseLeave={this.handleLeave}
        className="relative block"
        style={{
          width:  `${BOX_W + BOX_D}cqw`,           // viewport accounts for the box swinging out under rotation
          height: `${BOX_H + BOX_D * 0.6}cqw`,
          perspective: '110cqw',
          background: 'transparent',
          border: 'none',
        }}
      >
        {/* 3-D rig. Origin sits at the centre of the box */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top:  '50%',
            width: 0,
            height: 0,
            transformStyle: 'preserve-3d',
            transform: `translateY(${lifted ? -0.4 : 0}cqw) rotateX(${tiltX}deg) rotateY(${tiltY}deg)`,
            transition: 'transform 240ms cubic-bezier(.2,.7,.2,1)',
            willChange: 'transform',
          }}
        >
          {/* ── FRONT ─────────────────────────────────────────────── */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top:  0,
              width:  `${BOX_W}cqw`,
              height: `${BOX_H}cqw`,
              marginLeft: `${-halfW}cqw`,
              marginTop:  `${-halfH}cqw`,
              transform: `translateZ(${halfD}cqw)`,
              backfaceVisibility: 'hidden',
              overflow: 'hidden',
              borderRadius: 'var(--radius-sm)',
              background: '#0b0a08',
              boxShadow: 'inset 0 0 1px rgba(255,220,140,0.25)',
            }}
          >
            {/* Avatar art. Upper portion of the front face */}
            {artUrl ? (
              <img
                src={artUrl}
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '78%',
                  objectFit: 'cover',
                  objectPosition: 'center 18%',
                }}
              />
            ) : null}

            {/* Vignette + bottom-fade so the wordmark plaque sits on solid colour */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'radial-gradient(120% 80% at 50% 30%, transparent 55%, rgba(0,0,0,0.55) 100%),' +
                  'linear-gradient(180deg, transparent 55%, rgba(8,6,4,0.9) 78%, rgba(8,6,4,1) 100%)',
                pointerEvents: 'none',
              }}
            />

            {/* Bottom plaque */}
            <div
              style={{
                position: 'absolute',
                left: 0, right: 0, bottom: 0,
                paddingBottom: '0.55cqw',
                paddingTop: '0.6cqw',
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 25%, rgba(0,0,0,0.85) 100%)',
              }}
            >
              <div
                className="arena-heading"
                style={{
                  textAlign: 'center',
                  fontSize: '2.4cqw',
                  lineHeight: 1,
                  letterSpacing: '0.04em',
                  color: '#cfe5ff',
                  textShadow: `0 0 0.6cqw ${accent}, 0 0 0.18cqw ${accent}, 0 2px 3px rgba(0,0,0,0.95)`,
                  WebkitTextStroke: '0.4px rgba(0,0,0,0.6)',
                }}
              >
                {wordmark}
              </div>
              {/* Subtitle. Actual font-size shrinks (uniform on both
                  axes since letter-spacing is em-based) so every
                  avatar name fits inside the plaque without wrapping
                  and stays perfectly centred by text-align. Long
                  names (WATER) render at slightly smaller font than
                  short ones (AIR); padding on the two sides of each
                  individual box is identical, which is what matters. */}
              <div
                style={{
                  width: '82%',
                  margin: '0.25cqw auto 0',
                  textAlign: 'center',
                }}
              >
                <div
                  ref={this.subtitleRef}
                  className="arena-heading"
                  style={{
                    display: 'inline-block',
                    fontSize: `${0.75 * this.state.subtitleScale}cqw`,
 color: '#e8d5a0',
 textShadow: '0 1px 2px rgba(0,0,0,0.9)',
 whiteSpace: 'nowrap',
 }}
 >
 {subtitle}
 </div>
 </div>

 </div>

 <div
 style={{
 position: 'absolute',
 inset: 0,
 border: '1px solid rgba(190,150,70,0.55)',
 borderRadius: 'var(--radius-sm)',
 pointerEvents: 'none',
 }}
 />
 </div>

 {/* ── RIGHT SIDE ─────────────────────────────────────────── */}
 {this.renderSidePanel({
 transform: `rotateY(90deg) translateZ(${halfW}cqw)`,
            width:  BOX_D,
            height: BOX_H,
            shade: SIDE_SHADE,
            edgeHighlight: 'left',   // bright edge meets the front face on the right side
            withSeam: true,
          })}

          {/* ── LEFT SIDE. Backfilled so the box reads solid when tilted past 0° */}
          {this.renderSidePanel({
            transform: `rotateY(-90deg) translateZ(${halfW}cqw)`,
            width:  BOX_D,
            height: BOX_H,
            shade: LEFT_SHADE,
            edgeHighlight: 'right',
            withSeam: true,
          })}

          {/* ── TOP (lid) ─────────────────────────────────────────── */}
          {this.renderSidePanel({
            transform: `rotateX(90deg) translateZ(${halfH}cqw)`,
            width:  BOX_W,
            height: BOX_D,
            shade: TOP_SHADE,
            edgeHighlight: 'bottom', // bright edge meets the front face
          })}

          {/* ── BOTTOM ─────────────────────────────────────────────── */}
          {this.renderSidePanel({
            transform: `rotateX(-90deg) translateZ(${halfH}cqw)`,
            width:  BOX_W,
            height: BOX_D,
            shade: BOTTOM_SHADE,
            edgeHighlight: 'top',
          })}
        </div>

        {/* Cast shadow. Sits beneath the box, scales with hover lift */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '-0.4cqw',
            width:  `${BOX_W * 0.95}cqw`,
            height: '1.4cqw',
            transform: `translateX(-50%) scale(${lifted ? 1.08 : 1})`,
            background: 'radial-gradient(50% 100% at 50% 50%, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 70%)',
            filter: 'blur(0.2cqw)',
            transition: 'transform 240ms cubic-bezier(.2,.7,.2,1)',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
      </button>
    );
  }
}
